import { refreshAccessToken } from './api'
import { describeApiError } from './api-error'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export interface SseEvent {
  event: string
  data: any
}

const GENERIC_ERROR_MESSAGE = 'Spojenie s asistentom zlyhalo, skúste znova.'

/**
 * Spojenie padlo alebo request na server vôbec nedošiel (zatvorený notebook, výpadok siete). Ak správu
 * server prijal, odpoveď beží ďalej — AssistantContext preto pri známom chate namiesto chyby načíta jeho stav.
 */
export const ASSISTANT_CONNECTION_LOST = 'CONNECTION_LOST'

/**
 * Eventy, ktoré ukončujú jeden ťah konverzácie na serveri (po nich už server
 * nič nepošle). Ak stream skončí bez ani jedného z nich, spojenie zlyhalo
 * potichu (napr. proxy/network cut) — treba to nahlásiť ako chybu. `client_tool_required`
 * admin nikdy nedostane (posiela `clientTools: []`), no ošetruje sa rovnako pre istotu.
 */
const TERMINAL_EVENTS = new Set(['done', 'error', 'confirmation_required', 'client_tool_required'])

/** Rozdelí buffer na kompletné SSE eventy; nedokončený blok vráti ako `rest`. Komentáre (`: ping`) ignoruje. */
export function parseSseChunk(buffer: string): { events: SseEvent[]; rest: string } {
  // CRLF → LF: fetch/proxy môžu doručiť riadky s \r\n.
  const blocks = buffer.replace(/\r\n?/g, '\n').split('\n\n')
  const rest = blocks.pop() ?? ''
  const events: SseEvent[] = []
  for (const block of blocks) {
    let event = 'message'
    const data: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
    }
    if (data.length === 0) continue
    try {
      events.push({ event, data: JSON.parse(data.join('\n')) })
    } catch {
      // poškodený blok — preskočiť, stream pokračuje
    }
  }
  return { events, rest }
}

function headers(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h.Authorization = `Bearer ${token}`
  // Rovnako ako axios interceptor (lib/api.ts) — SUPER_ADMIN tenant override.
  const tenantId = localStorage.getItem('superadmin_tenant_id')
  if (tenantId) h['X-Tenant-ID'] = tenantId
  return h
}

/**
 * Non-OK odpoveď → človeku čitateľná správa (+ voliteľný `code` pre AssistantContext,
 * napr. AI_CONSENT_REQUIRED, ktorý spustí refresh usera aby sa zobrazila súhlasová brána).
 */
async function describeHttpError(res: Response): Promise<{ message: string; code?: string }> {
  if (res.status === 429) return { message: 'Príliš veľa správ za krátky čas — počkajte chvíľu a skúste znova.' }
  if (res.status === 403) {
    try {
      const j = await res.json()
      if (j?.error === 'AI_CONSENT_REQUIRED') {
        return { message: 'Pred použitím asistenta udeľte súhlas so zdieľaním dát.', code: 'AI_CONSENT_REQUIRED' }
      }
      if (j?.error === 'AI_ASSISTANT_DISABLED') {
        return { message: 'AI asistent vám bol vypnutý v nastaveniach tímu.', code: 'AI_ASSISTANT_DISABLED' }
      }
    } catch {
      // telo nie je JSON / je prázdne
    }
    return { message: 'Nemáte oprávnenie používať AI asistenta.' }
  }
  const body = await res.json().catch(() => null) // telo nie je JSON / je prázdne
  return { message: describeApiError(res.status, body, GENERIC_ERROR_MESSAGE) }
}

function isAbortError(err: unknown, signal: AbortSignal): boolean {
  return signal.aborted || (err instanceof Error && err.name === 'AbortError')
}

/**
 * POST /assistant/chat a čítanie SSE odpovede. EventSource nevie POST, preto fetch + stream.
 * Pri 401 raz obnoví token a request zopakuje (server pri 401 kredit nestrhol). Ak sa token
 * obnoviť nedá, správa sa rovnako ako axios interceptor (lib/api.ts) pri zlyhanom refreshi —
 * zmaže accessToken a presmeruje na /login, bez ďalšieho eventu.
 *
 * Odtiaľto von vedie presne jedna cesta pre chyby — event `error` — a jedna tichá cesta pre
 * zrušenie (AbortController.abort()). Transportné chyby aj stream, ktorý skončí bez
 * terminálneho eventu (done/error/confirmation_required/client_tool_required), sa hlásia
 * ako `error`; prerušenie requestu volajúcim nikdy nič nevolá.
 */
export async function streamAssistantChat(
  body: object,
  onEvent: (e: SseEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const send = (token: string | null) =>
    fetch(`${API_BASE}/api/v1/assistant/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: headers(token),
      body: JSON.stringify(body),
      signal,
    })

  try {
    let res = await send(localStorage.getItem('accessToken'))
    if (res.status === 401) {
      let newToken: string
      try {
        newToken = await refreshAccessToken()
      } catch {
        // Zhoduje sa s axios interceptorom (lib/api.ts) — refresh zlyhal, treba sa znova prihlásiť.
        localStorage.removeItem('accessToken')
        window.location.href = '/login'
        return
      }
      res = await send(newToken)
    }

    if (!res.ok || !res.body) {
      const { message, code } = await describeHttpError(res)
      // Čítanie tela mohlo trvať — ak medzitým používateľ request zrušil, mlčať.
      if (signal.aborted) return
      onEvent({ event: 'error', data: { message, ...(code ? { code } : {}) } })
      return
    }

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
    let buffer = ''
    let sawTerminal = false
    const emit = (e: SseEvent) => {
      if (TERMINAL_EVENTS.has(e.event)) sawTerminal = true
      onEvent(e)
    }
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += value
      const { events, rest } = parseSseChunk(buffer)
      buffer = rest
      events.forEach(emit)
    }
    // Server každý blok ukončuje \n\n (`openSse().write` v assistant-sse.ts); flush zvyšku je len
    // obrana pre prípad, že proxy/transport posledný oddeľovač odreže.
    parseSseChunk(`${buffer}\n\n`).events.forEach(emit)

    if (!sawTerminal && !signal.aborted) {
      onEvent({ event: 'error', data: { message: GENERIC_ERROR_MESSAGE, code: ASSISTANT_CONNECTION_LOST } })
    }
  } catch (err) {
    if (isAbortError(err, signal)) return
    onEvent({ event: 'error', data: { message: GENERIC_ERROR_MESSAGE, code: ASSISTANT_CONNECTION_LOST } })
  }
}
